/**
 * Consent calendar rows on the agenda (distinct from unanimous-consent requests on the floor).
 */

export const CONSENT_CALENDAR_AGENDA_TYPE = 'consent_calendar';

/** Agenda row for a main motion deferred from the consent calendar until after adoption. */
export const MAIN_FROM_CONSENT_AGENDA_TYPE = 'main_from_consent';

export function isConsentCalendarAgendaRow(row) {
  return row?.type === CONSENT_CALENDAR_AGENDA_TYPE;
}

export function isMainFromConsentAgendaRow(row) {
  return row?.type === MAIN_FROM_CONSENT_AGENDA_TYPE;
}

export function getActiveConsentItems(consentAgendaItems = []) {
  return consentAgendaItems.filter((i) => i && !i.pulledToFloor);
}

export function meetingAgendaHasConsentCalendar(orderOfBusiness = []) {
  return orderOfBusiness.some(isConsentCalendarAgendaRow);
}

function findNewBusinessIndex(order) {
  const idx = order.findIndex((r) => /new business/i.test(String(r?.title || '')));
  return idx >= 0 ? idx : Math.max(0, order.length - 2);
}

/**
 * Adds a consent calendar row if missing (typically before New Business).
 */
export function insertConsentCalendarRow(orderOfBusiness) {
  if (meetingAgendaHasConsentCalendar(orderOfBusiness)) return orderOfBusiness;
  const copy = [...orderOfBusiness];
  const idx = findNewBusinessIndex(copy);
  const row = {
    id: `consent-${Date.now()}`,
    type: CONSENT_CALENDAR_AGENDA_TYPE,
    title: 'Consent Calendar',
    completed: false,
  };
  copy.splice(idx, 0, row);
  return copy;
}

export function removeConsentCalendarRows(orderOfBusiness) {
  return orderOfBusiness.filter((r) => !isConsentCalendarAgendaRow(r));
}

export function applyConsentToggleToAgenda(orderOfBusiness, enabled) {
  if (enabled) return insertConsentCalendarRow(orderOfBusiness);
  return removeConsentCalendarRows(orderOfBusiness);
}

/** Deep-enough clone for a new session: new ids, reset completed. */
export function cloneAgendaForNewSession(items) {
  const base = Date.now();
  return (items || []).map((item, i) => ({
    ...item,
    id: base + i + Math.floor(Math.random() * 1000),
    completed: false,
  }));
}

/** One proposed item on the consent calendar (adopted by general consent unless pulled). */
export function createConsentLineItem(text = '') {
  return {
    id: `cline-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    text,
    pulledToFloor: false,
  };
}

export function cloneConsentLinesTemplate(lines) {
  try {
    return JSON.parse(JSON.stringify(lines || []));
  } catch {
    return (lines || []).map((l) => ({ ...l, text: l.text || '' }));
  }
}

/** Fresh ids for a new meeting session (copy from org default → this meeting). */
export function cloneConsentLinesForMeeting(lines) {
  const base = Date.now();
  return (lines || []).map((line, i) => ({
    id: `c-${base}-${i}-${Math.random().toString(36).slice(2, 7)}`,
    text: typeof line?.text === 'string' ? line.text : '',
    pulledToFloor: false,
  }));
}

export const PARLIAMENTARY_MODES = ['standard', 'small_board', 'convention', 'committee'];

export function defaultOrganizationSettings(baseOrderOfBusiness) {
  return {
    allowConsentAgenda: false,
    enableRollCallRoster: false,
    defaultOrderOfBusiness: cloneAgendaTemplateFromOrder(baseOrderOfBusiness),
    defaultConsentLines: [],
    defaultUnanimousConsentRequests: createStandardUnanimousConsentRequests(),
    defaultQuorumType: 'percentage',
    defaultQuorumValue: 50,
    defaultParliamentaryMode: 'standard',
  };
}

export function cloneAgendaTemplateFromOrder(order) {
  try {
    return JSON.parse(JSON.stringify(order || []));
  } catch {
    return (order || []).map((item, i) => ({ ...item, id: item.id ?? i + 1 }));
  }
}

/** Built-in UC shortcuts when org has not customized the list. */
export function createStandardUnanimousConsentRequests() {
  return [
    'Recess for 5 minutes',
    'Recess for 10 minutes',
    'Close nominations',
    'Extend speaking time by 2 minutes',
    'Suspend the rules',
    'Take from the table',
  ];
}

export function cloneUnanimousConsentRequests(list) {
  if (!Array.isArray(list)) return createStandardUnanimousConsentRequests();
  return list.map((item) => String(item ?? '').trim()).filter(Boolean);
}

/** Standard RONR-style seed agenda used when bylaws_config has no template. */
export function createStandardOrderOfBusiness() {
  return [
    { id: 1, type: 'standard', title: 'Call to Order', completed: false },
    { id: 2, type: 'standard', title: 'Roll Call', completed: false },
    { id: 3, type: 'standard', title: 'Reading of Minutes', completed: false },
    { id: 4, type: 'standard', title: 'President Report', completed: false },
    { id: 5, type: 'standard', title: 'Secretary Report', completed: false },
    { id: 6, type: 'standard', title: 'Treasurer Report', completed: false },
    { id: 7, type: 'standard', title: 'Committee Reports', completed: false },
    { id: 8, type: 'standard', title: 'Unfinished Business', completed: false },
    { id: 9, type: 'standard', title: 'New Business', completed: false },
    { id: 10, type: 'standard', title: 'Elections', completed: false },
    { id: 11, type: 'standard', title: 'Announcements', completed: false },
    { id: 12, type: 'standard', title: 'Adjournment', completed: false },
  ];
}

/**
 * Parse organizations.bylaws_config into in-app organizationSettings.
 * Unknown / empty config falls back to the standard agenda with consent off.
 */
export function organizationSettingsFromBylaws(bylawsConfig) {
  const fallbackOrder = createStandardOrderOfBusiness();
  const cfg =
    bylawsConfig && typeof bylawsConfig === 'object' && !Array.isArray(bylawsConfig)
      ? bylawsConfig
      : {};

  const rawOrder = Array.isArray(cfg.defaultOrderOfBusiness)
    ? cfg.defaultOrderOfBusiness
    : fallbackOrder;
  let defaultOrderOfBusiness = cloneAgendaTemplateFromOrder(rawOrder);
  if (!defaultOrderOfBusiness.length) {
    defaultOrderOfBusiness = cloneAgendaTemplateFromOrder(fallbackOrder);
  }

  const allowConsentAgenda = Boolean(cfg.allowConsentAgenda);
  defaultOrderOfBusiness = applyConsentToggleToAgenda(
    defaultOrderOfBusiness,
    allowConsentAgenda
  );

  const defaultConsentLines = allowConsentAgenda
    ? cloneConsentLinesTemplate(
        Array.isArray(cfg.defaultConsentLines) ? cfg.defaultConsentLines : []
      )
    : [];

  const defaultUnanimousConsentRequests = Array.isArray(cfg.defaultUnanimousConsentRequests)
    ? cloneUnanimousConsentRequests(cfg.defaultUnanimousConsentRequests)
    : createStandardUnanimousConsentRequests();

  const defaultQuorumType =
    cfg.defaultQuorumType === 'fixed' || cfg.defaultQuorumType === 'percentage'
      ? cfg.defaultQuorumType
      : 'percentage';
  const parsedQuorum = Number(cfg.defaultQuorumValue);
  const defaultQuorumValue = Number.isFinite(parsedQuorum)
    ? Math.max(0, parsedQuorum)
    : 50;
  const defaultParliamentaryMode = PARLIAMENTARY_MODES.includes(cfg.defaultParliamentaryMode)
    ? cfg.defaultParliamentaryMode
    : 'standard';

  return {
    allowConsentAgenda,
    enableRollCallRoster: Boolean(cfg.enableRollCallRoster),
    defaultOrderOfBusiness,
    defaultConsentLines,
    defaultUnanimousConsentRequests,
    defaultQuorumType,
    defaultQuorumValue,
    defaultParliamentaryMode,
  };
}

/** Serialize organizationSettings for organizations.bylaws_config. */
export function bylawsConfigFromOrganizationSettings(settings) {
  const org = settings || defaultOrganizationSettings(createStandardOrderOfBusiness());
  const quorumType =
    org.defaultQuorumType === 'fixed' || org.defaultQuorumType === 'percentage'
      ? org.defaultQuorumType
      : 'percentage';
  const quorumValue = Number(org.defaultQuorumValue);
  return {
    allowConsentAgenda: Boolean(org.allowConsentAgenda),
    enableRollCallRoster: Boolean(org.enableRollCallRoster),
    defaultOrderOfBusiness: cloneAgendaTemplateFromOrder(org.defaultOrderOfBusiness || []),
    defaultConsentLines: cloneConsentLinesTemplate(org.defaultConsentLines || []),
    defaultUnanimousConsentRequests: cloneUnanimousConsentRequests(
      org.defaultUnanimousConsentRequests
    ),
    defaultQuorumType: quorumType,
    defaultQuorumValue: Number.isFinite(quorumValue) ? Math.max(0, quorumValue) : 50,
    defaultParliamentaryMode: PARLIAMENTARY_MODES.includes(org.defaultParliamentaryMode)
      ? org.defaultParliamentaryMode
      : 'standard',
  };
}

/** Build a fresh meeting agenda + consent lines from org settings. */
export function meetingAgendaFromOrganizationSettings(organizationSettings) {
  const org =
    organizationSettings || defaultOrganizationSettings(createStandardOrderOfBusiness());
  let order = cloneAgendaForNewSession(
    org.defaultOrderOfBusiness?.length
      ? org.defaultOrderOfBusiness
      : createStandardOrderOfBusiness()
  );
  order = applyConsentToggleToAgenda(order, !!org.allowConsentAgenda);
  const consentAgendaItems = org.allowConsentAgenda
    ? cloneConsentLinesForMeeting(org.defaultConsentLines || [])
    : [];
  return { orderOfBusiness: order, consentAgendaItems };
}
